'use client';

/* ──────────────────────────────────────────────
 *  BookingSummary — adaptateur : projette le contexte
 *  de réservation (`useBooking`) sur le composant
 *  présentationnel partagé `TunnelSummary` (mutualisé
 *  avec les tunnels carte cadeau et VTC).
 *  Deux points de rendu :
 *  - `sidebar` (layout wide) — CTA + retour issus du StepNav
 *  - `sheet` (colonne unique) — contenu de la feuille dépliée par le StepFooter
 *  Montants du bloc `amounts` (pré-réservation, sinon résumé du
 *  panier) : lignes au prix normal (formule et durée), remise sur
 *  sa ligne, total après remise avec le prix normal barré, champ
 *  code, bons, puis à payer en ligne et reste sur place. Résumé du
 *  panier en échec : ni prix ni total périmés, un message et
 *  « Réessayer » (bug 11).
 * ────────────────────────────────────────────── */

import { useMemo } from 'react';
import { resolveSupplementQuantity } from '@cfixe-monorepo/shared';
import { useBooking } from '../context/BookingContext';
import { useBookingCodes } from '../context/BookingCodesContext';
import { isMemberChoiceSkippable } from '../context/booking-machine';
import { useStepNav } from '../context/StepNavContext';
import type { BookingStep, CartItem } from '../types';
import { discountRowsOf, voucherRowsOf } from '../utils/amount-lines';
import { chosenMemberNames } from '../utils/member-eligibility';
import { cartHasLowerBoundPricing, hasNonZeroFormattedAmount, withFromPrefix } from '../utils/payment-amounts';
import { lineDetailSublines, partySizeSuffix, recapLinePrice } from '../utils/recap-lines';
import { computeSummaryStruckTotal, computeSummaryTotalValue } from '../utils/summary-total';
import { BookingCodeField } from './shared/BookingCodeField';
import {
  TunnelSummary,
  type TunnelSummaryBackAction,
  type TunnelSummaryContextRow,
  type TunnelSummaryCta,
  type TunnelSummaryItem,
  type TunnelSummaryPaymentRow,
  type TunnelSummarySupplement,
} from './shared/TunnelSummary';
import { toSupplementSelectionPayload } from '../utils/supplement-selection';

interface BookingSummaryProps {
  variant: 'sidebar' | 'sheet';
  /** Sans le champ code (carte récap de la finalisation, qui a déjà le sien dans le formulaire). */
  withoutCode?: boolean;
}

/* Le champ code apparaît dès l'étape créneau (spec Promotions § 8). */
const CODE_FIELD_STEPS = new Set<BookingStep | 'loading' | 'error'>(['slot', 'checkout']);

export function BookingSummary({ variant, withoutCode = false }: BookingSummaryProps): React.JSX.Element | null {
  const { ctx, send, labels, currentStep } = useBooking();
  const { amounts, codes, summaryError, retrySummary } = useBookingCodes();
  const stepNav = useStepNav();
  const {
    cartItems,
    cartSummary,
    cartSummaryLoading,
    selectedPlaceId,
    availablePlaces,
    availableMembers,
    memberAssignments,
    selectedSlot,
    customerAddress,
    preReservationData,
  } = ctx;

  const selectedPlace = useMemo(
    () => availablePlaces.find((p): boolean => p.pro_place_id === selectedPlaceId),
    [availablePlaces, selectedPlaceId],
  );

  // Montant « plancher » : QUOTE/FROM ou NONE (presta sans prix mêlée à des prestas chiffrées) →
  // on préfixe « à partir de » car le sous-total connu n'est pas le prix final.
  const hasLowerBoundPricing = useMemo((): boolean => cartHasLowerBoundPricing(cartItems), [cartItems]);

  // Écran de succès : il porte son propre récap ; le panier, lui, est déjà vidé ou périmé.
  // Demande de devis : hors parcours, le récap de la sélection n'y a pas sa place.
  if (cartItems.length === 0 || currentStep === 'success' || currentStep === 'quote') return null;

  const showRemove = currentStep === 'services';

  /* Résumé du panier en échec (bug 11) : ses lignes et ses montants seraient périmés. Les lignes viennent alors
     du panier local, sans prix, et un message avec « Réessayer » prend la place du total. */
  const summary = summaryError ? undefined : cartSummary;
  const shownAmounts = summaryError ? undefined : amounts;

  /* Sous-ligne « 4 × 20,00 € » : le multiplicateur et le prix unitaire viennent TOUS DEUX du backend
     (le prix est déjà formaté serveur — jamais recomposé ici). Le backend ne les renseigne qu'au-delà
     d'une unité facturée, donc l'absence du champ suffit à ne rien afficher. */
  const unitBreakdownSublines = (item: { line_units?: number; line_unit_price?: string }): string[] =>
    item.line_units && item.line_unit_price
      ? [
          labels.line_unit_breakdown
            .replace('{count}', String(item.line_units))
            .replace('{price}', item.line_unit_price),
        ]
      : [];

  /* Suppléments d'une ligne en attendant le récap serveur : même quantité que celle qu'il facturera (choisie,
     sinon une par unité facturée de la ligne). */
  const localSupplementLines = (item: CartItem): TunnelSummarySupplement[] => {
    const line = { ...item, ...toSupplementSelectionPayload(item) };
    return item.supplements
      .filter((s): boolean => s.selected)
      .map(
        (s): TunnelSummarySupplement => ({
          name: s.name,
          quantity: resolveSupplementQuantity(line, s.supplement_id),
        }),
      );
  };

  const items: TunnelSummaryItem[] = summary
    ? summary.items.map(
        (item, i): TunnelSummaryItem => ({
          // Le libellé backend ne porte que la quantité : on y ajoute la taille du groupe, qui
          // vient du panier local (même index — hypothèse déjà faite par REMOVE_FROM_CART). Rappel « · N pers. » :
          // party_size multiplie le prix côté backend, sans lui le montant multiplié ne s'explique pas.
          label: `${item.line_label}${partySizeSuffix(cartItems[i]?.party_size, labels.party_size_suffix)}`,
          badge: item.discount_label,
          // NONE (« aucun prix ») : forme courte « À déterminer » sur la ligne pour lever l'ambiguïté
          // dans un panier mixte (la ligne sans prix ne reste pas vide à côté d'une ligne chiffrée).
          price: recapLinePrice(item.price_type, item.line_price, labels),
          // Formule et durée de la ligne (spec Parcours § 9, ligne 1), puis la décomposition « 4 × 20,00 € ».
          sublines: [...lineDetailSublines(item), ...unitBreakdownSublines(item)],
          supplements: item.supplements.map(
            (s): TunnelSummarySupplement => ({ name: s.name, price: s.price, quantity: s.quantity }),
          ),
          ...(showRemove ? { onRemove: (): void => send({ type: 'REMOVE_FROM_CART', index: i }) } : {}),
        }),
      )
    : cartItems.map(
        (item, i): TunnelSummaryItem => ({
          label: `${item.prestation.name}${item.quantity > 1 ? ` x${item.quantity}` : ''}${partySizeSuffix(
            item.party_size,
            labels.party_size_suffix,
          )}`,
          ...(cartSummaryLoading && !summaryError ? { price: '…' } : {}),
          supplements: localSupplementLines(item),
          ...(showRemove ? { onRemove: (): void => send({ type: 'REMOVE_FROM_CART', index: i }) } : {}),
        }),
      );

  /* Liens « modifier » (spec Parcours § 6 décision 3) : ils rouvrent l'étape, la pré-réservation est libérée. */
  const canEdit = currentStep === 'slot' || currentStep === 'checkout';
  const editRow = (step: 'place' | 'zone_address' | 'slot', text: string): Partial<TunnelSummaryContextRow> =>
    canEdit && !(step === 'slot' && currentStep === 'slot')
      ? {
          onEdit: (): void => send({ type: 'EDIT_STEP', step }),
          editLabel: labels.edit,
          editAriaLabel: labels.edit_aria.replace('{item}', text),
        }
      : {};
  const contextRows: TunnelSummaryContextRow[] = [];
  if (selectedPlace) {
    contextRows.push({
      icon: '📍',
      text: selectedPlace.label,
      ...(availablePlaces.length > 1 ? editRow('place', selectedPlace.label) : {}),
    });
  }
  if (customerAddress?.formatted_address) {
    contextRows.push({
      icon: '🏠',
      text: customerAddress.formatted_address,
      ...editRow('zone_address', customerAddress.formatted_address),
    });
  }
  /* Membre choisi au-dessus du calendrier (spec Parcours § 9, ligne 2) : seulement quand le client avait le choix
     et a choisi quelqu'un (« Sans préférence » n'affiche rien) ; « modifier » ramène à l'étape créneau. */
  const memberNames = isMemberChoiceSkippable(ctx) ? [] : chosenMemberNames(memberAssignments, availableMembers);
  if (memberNames.length > 0) {
    const memberText = labels.with_member.replace('{name}', memberNames.join(', '));
    contextRows.push({ icon: '👤', text: memberText, ...editRow('slot', memberText) });
  }
  // Date ET heure du créneau (bug 19) ; l'heure seule pour un backend antérieur.
  const slotText = selectedSlot?.slot_start_string ?? selectedSlot?.slot_start_time_string;
  if (slotText) contextRows.push({ icon: '📅', text: slotText, ...editRow('slot', slotText) });

  const discountRows: TunnelSummaryPaymentRow[] = discountRowsOf(shownAmounts, labels);
  const voucherRows: TunnelSummaryPaymentRow[] = voucherRowsOf(shownAmounts, labels);
  /* Panier gratuit : rien à remiser, pas de champ (sauf pour retirer un code encore posé). */
  const isFreeCart = shownAmounts?.gross_total_raw === 0 && !codes.promo && !codes.voucher;
  const codeSlot = CODE_FIELD_STEPS.has(currentStep) && !isFreeCart && !withoutCode ? <BookingCodeField /> : undefined;

  const paymentInfos = preReservationData?.payment_infos;
  const amountToPayToday = preReservationData?.amounts?.amount_to_pay_today ?? paymentInfos?.amount_to_pay_today;
  const remainingOnSite = preReservationData?.amounts
    ? preReservationData.amounts.remaining_on_site_raw
      ? preReservationData.amounts.remaining_on_site
      : undefined
    : paymentInfos?.remaining_amount_on_site;
  const remainingOnSiteDisplay = hasNonZeroFormattedAmount(remainingOnSite) ? remainingOnSite : undefined;
  const paymentRows: TunnelSummaryPaymentRow[] = [];
  if (paymentInfos && amountToPayToday) {
    paymentRows.push({ label: labels.amount_to_pay, value: amountToPayToday });
    if (remainingOnSiteDisplay) {
      paymentRows.push({
        label: labels.remaining_on_site,
        value: withFromPrefix(remainingOnSiteDisplay, hasLowerBoundPricing, labels.from_price_prefix),
        highlight: true,
      });
    }
  }

  /* Sidebar wide : l'action principale et le retour viennent du StepNav de l'étape courante. */
  const cta: TunnelSummaryCta | undefined =
    variant === 'sidebar' && stepNav?.primary && !stepNav.ownsPrimary
      ? {
          label: stepNav.primary.label,
          onClick: stepNav.primary.onClick,
          disabled: stepNav.primary.disabled,
          loading: stepNav.primary.loading,
        }
      : undefined;
  const backAction: TunnelSummaryBackAction | undefined =
    variant === 'sidebar' && stepNav?.onBack ? { label: labels.back, onClick: stepNav.onBack } : undefined;

  return (
    <TunnelSummary
      variant={variant}
      items={items}
      contextRows={contextRows}
      totalValue={computeSummaryTotalValue(summary, cartSummaryLoading, labels, shownAmounts, summaryError)}
      totalStruck={computeSummaryStruckTotal(shownAmounts, summaryError)}
      totalDuration={summary?.total_duration}
      totalNotice={
        summaryError
          ? {
              text: labels.summary_error,
              actionLabel: labels.retry,
              onAction: retrySummary,
              pending: cartSummaryLoading,
            }
          : undefined
      }
      discountRows={discountRows}
      codeSlot={codeSlot}
      voucherRows={voucherRows}
      paymentRows={paymentRows}
      cta={cta}
      backAction={backAction}
      labels={{
        // « Votre rendez-vous » pour une prestation, « Votre sélection » en multi (spec Parcours § 5).
        selection_title: ctx.entryMode === 'multi' ? labels.selection_title : labels.summary_title_single,
        total_label: labels.total_label,
        remove: labels.remove,
      }}
    />
  );
}
